import React, { useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { MetricStrip } from '../common/MetricStrip';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { REPORT_TEMPLATES } from '../../utils/constants';
import { fmtDate, relTime, nowISO } from '../../utils/helpers';
import ReportGenerateModal from './ReportGenerateModal';
import BABEFeasibilityReportPreview from './BABEFeasibilityReportPreview';
import { exportReportDOCX, exportReportPDF } from './reportExport';
import { reportServices } from '../../APIServices/reportServices';

function ReportsModule({ onNavigate }) {
  const { state, loadReportList, audit, currentUser } = useData();
  const toast = useToast();
  const [showGenerate, setShowGenerate] = useState(false);
  const [activeReport, setActiveReport] = useState(null);

  const handleGenerate = async (cfg) => {
    const study = state.studies.find((s) => s.id === cfg.studyId);
    const tpl = REPORT_TEMPLATES.find((t) => t.id === cfg.templateId);
    const sources = state.knowledge.filter(
      (k) => k.status === 'approved' && !k.deleted &&
      (!study?.molecule || k.molecule?.toLowerCase() === study.molecule?.toLowerCase() || k.collection === 'regulatory' || k.collection === 'historical')
    );

    const payload = {
      name: `${tpl.name} — ${study?.code}`,
      templateId: cfg.templateId,
      studyId: cfg.studyId,
      feasibilityId: cfg.feasibilityId,
      sourceIds: sources.map((s) => s.id),
      status: 'draft',
      generatedAt: nowISO(),
      generatedBy: currentUser.id,
      engineVersion: 'v2.3.1',
      rulePack: 'v14',
      templateVersion: 'v8'
    };

    const data = await reportServices.addNewReport(payload);
    const report = data?.data;

    await loadReportList();
    audit('REPORT_GENERATE', report?.code || 'RPT', `${tpl.name} for ${study?.code}`, { reportId: report?.id, studyId: cfg.studyId });
    toast(`Report ${report?.code || ''} generated`, 'ok');
    setActiveReport(report);
  };

  const handleApprove = async (report) => {
    const data = await reportServices.updateReport({
      id: report._id || report.id,
      reportId: report.reportId,
      status: 'approved',
      approvedAt: nowISO(),
      approvedBy: currentUser.id,
    });

    const updated = data?.data || { ...report, status: 'approved', approvedAt: nowISO(), approvedBy: currentUser.id };
    await loadReportList();
    audit('REPORT_APPROVE', report.code, 'Report approved for use', { reportId: report.id });
    toast(`${report.code} approved`, 'ok');
    setActiveReport(updated);
  };

  if (activeReport) {
    const study = state.studies.find((s) => s.id === activeReport.studyId);
    const feasibility = state.feasibilities.find((f) => f.id === activeReport.feasibilityId);
    const sources = state.knowledge.filter((k) => activeReport.sourceIds?.includes(k.id));
    const risks = state.risks;

    return (
      <div>
        <div className="flex gap-2 center mb-3">
          <button className="btn btn-sm btn-ghost" onClick={() => setActiveReport(null)}>
            <i className="ti ti-arrow-left" /> Back to reports
          </button>
        </div>

        <div className="page-header">
          <div>
            <div className="page-eyebrow">
              {activeReport.code} · {REPORT_TEMPLATES.find((t) => t.id === activeReport.templateId)?.name}
            </div>
            <h1 className="page-title" style={{ fontSize: 26 }}>{activeReport.name}</h1>
            <div className="flex gap-2 mt-2 center">
              {activeReport.status === 'approved' ? (
                <Tag variant="ok" dot>Approved</Tag>
              ) : (
                <Tag variant="warn" dot>Draft</Tag>
              )}
              <span className="xs muted">Generated {fmtDate(activeReport.generatedAt)}</span>
              <span className="xs muted">· Engine {activeReport.engineVersion}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn" onClick={() => exportReportDOCX(activeReport, study, feasibility, sources, risks, state)}>
              <i className="ti ti-file-type-doc" /> Export DOCX
            </button>
            <button className="btn" onClick={() => exportReportPDF(activeReport)}>
              <i className="ti ti-file-type-pdf" /> Export PDF
            </button>
            {activeReport.status !== 'approved' && (
              <button className="btn btn-primary" onClick={() => handleApprove(activeReport)}>
                <i className="ti ti-check" /> Approve report
              </button>
            )}
          </div>
        </div>

        <BABEFeasibilityReportPreview
          study={study}
          feasibility={feasibility}
          sources={sources}
          risks={risks}
          draft={activeReport.status !== 'approved'}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 15.7 · Standard reports</div>
          <h1 className="page-title">Report generation</h1>
          <p className="page-sub">Templated reports pre-filled from approved data — never from raw model output. Every claim cites a source; every report has a reproducible signature.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>
          <i className="ti ti-file-text" /> Generate report
        </button>
      </div>

      <MetricStrip
        items={[
          { label: 'Templates', value: REPORT_TEMPLATES.length, foot: 'standard library' },
          { label: 'Generated', value: state.reports.length, foot: 'all time' },
          { label: 'Approved', value: state.reports.filter((r) => r.status === 'approved').length, foot: 'locked, exportable' },
          { label: 'Drafts', value: state.reports.filter((r) => r.status === 'draft').length, foot: 'pending review', dir: 'warn' }
        ]}
      />

      <div className="section-label mt-4 mb-5">
        Templates <span className="count">{REPORT_TEMPLATES.length}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {REPORT_TEMPLATES.map((t) => (
          <div key={t.id} className="card card-pad" style={t.primary ? { borderColor: 'var(--ink)' } : null}>
            <div className="flex between center mb-2">
              <div className="mono xs muted">{t.code}</div>
              {t.primary && <Tag variant="solid">Primary</Tag>}
            </div>
            <div className="medium mb-1" style={{ fontFamily: 'var(--display)', fontSize: 16 }}>{t.name}</div>
            <div className="xs muted mb-3">{t.desc}</div>
            <div className="flex gap-1" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
              {t.sections.slice(0, 4).map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
              {t.sections.length > 4 && <Tag>+{t.sections.length - 4}</Tag>}
            </div>
            <button className="btn btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setShowGenerate(true)}>
              <i className="ti ti-plus" /> Generate from this template
            </button>
          </div>
        ))}
      </div>

      <div className="section-label mt-4 mb-5">
        Recent reports <span className="count">{state.reports.length}</span>
      </div>

      <div className="card">
        {state.reports.length === 0 ? (
          <EmptyState
            icon="ti-file-off"
            title="No reports yet"
            desc="Generate your first report from approved data."
            action={<button className="btn btn-primary" onClick={() => setShowGenerate(true)}><i className="ti ti-file-text" /> Generate report</button>}
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Template</th>
                <th>Status</th>
                <th>Generated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.reports.map((r) => {
                const tpl = REPORT_TEMPLATES.find((t) => t.id === r.templateId);
                return (
                  <tr key={r.id} onClick={() => setActiveReport(r)} style={{ cursor: 'pointer' }}>
                    <td className="id">{r.code}</td>
                    <td className="medium">{r.name}</td>
                    <td>{tpl?.name}</td>
                    <td>
                      {r.status === 'approved' ? (
                        <Tag variant="ok" dot>Approved</Tag>
                      ) : (
                        <Tag variant="warn" dot>Draft</Tag>
                      )}
                    </td>
                    <td className="xs muted">{relTime(r.generatedAt)}</td>
                    <td><i className="ti ti-chevron-right muted" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ReportGenerateModal open={showGenerate} onClose={() => setShowGenerate(false)} onGenerate={handleGenerate} />
    </div>
  );
}

export default ReportsModule;